import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, Fragment, useContext } from 'react';
/**
 * Module 99 (D2) — the game's coin, drawn wherever a coin amount is SEEN.
 *
 * Until Module 99 a cost was written `"500 🪙"`, and every OS drew that emoji
 * in its own font: a gold coin on one platform, Apple's grey coin on another,
 * neither of them this game's coin (user, 2026-10-08, `Bugs/Coin_*.jpg`). The
 * coin is now the shared `coin.png` (`coinIconUrl`, Module 44), the same art as
 * the Top Bar badge and the Win popup.
 *
 * A translated sentence still owns where the coin goes ("{{cost}} {{coin}}" in
 * English, after the verb in Turkish): the code passes {@link COIN_MARK} for
 * `coin`, and {@link CoinText} splits the result on it. Aria labels keep the
 * plain `formatCost` string — only what is drawn changes (D5).
 */
/** A private-use character: never in a translation, so it can only be ours. */
export const COIN_MARK = String.fromCharCode(0xe000);
const CoinIconUrlContext = createContext(undefined);
/** Provided once by `GameHUD`, so no component in between carries the URL. */
export function CoinIconProvider({ url, children, }) {
    return _jsx(CoinIconUrlContext.Provider, { value: url, children: children });
}
/**
 * Module 101 (WP-101.7) — the injected coin art's URL, for a picture that draws
 * the coin inside an SVG (`<image href>`) rather than as an `<img>`: the How to
 * Play Coins and Daily demos. Undefined where no URL was injected.
 */
export function useCoinIconUrl() {
    return useContext(CoinIconUrlContext);
}
/**
 * The coin art, or a CSS-drawn coin where no URL was injected (tests, a host
 * without the asset) — as `RevealConfirmCard` does. Never the emoji.
 */
export function CoinIcon() {
    const url = useContext(CoinIconUrlContext);
    return url ? (_jsx("img", { className: "bj-coin", src: url, alt: "", "aria-hidden": "true", "data-testid": "bj-coin" })) : (_jsx("span", { className: "bj-coin bj-coin--drawn", "aria-hidden": "true", "data-testid": "bj-coin" }));
}
/** `"500 <mark>"`, `"5 💎"`, or both — `formatCost`'s drawn twin. */
export function formatCostMarked(cost) {
    const parts = [];
    if (cost.coins > 0)
        parts.push(`${cost.coins} ${COIN_MARK}`);
    if (cost.gems > 0)
        parts.push(`${cost.gems} 💎`);
    return parts.join(' · ');
}
/** Renders `text` with every {@link COIN_MARK} replaced by the coin art. */
export function CoinText({ text }) {
    const pieces = text.split(COIN_MARK);
    if (pieces.length === 1)
        return _jsx(_Fragment, { children: text });
    return (_jsx(_Fragment, { children: pieces.map((piece, i) => (_jsxs(Fragment, { children: [i > 0 && _jsx(CoinIcon, {}), piece] }, i))) }));
}
